import React, { useEffect, useState } from 'react';
import { DoorOpen, Loader2, Save } from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from './lib/permissions';

// Oyunun kendi arayüzündeki "lobi / ana sayfa" düğmesine basıldığında ne
// olacağı. Değer `settings` kaydında değil `provider_configs`'tedir (bkz.
// backend/services/game_lobby.py), bu yüzden sekmenin ortak Kaydet düğmesine
// bağlanmaz: kendi ucu ve düğmesi vardır.
export const GAME_LOBBY_DEFAULT = 'relaunch';

export const LOBBY_HINTS = {
  relaunch: 'Lobiye geçilmez: düğmeye basan oyuncu aynı oyuna taze bir '
    + 'açılışla geri döner. Varsayılan davranış budur — oyuncuya "lobi '
    + 'düğmesi çalışmıyor" gibi görünür.',
  site: 'Oyun kapanır ve oyuncu sitenin kendi oyun listesine döner. '
    + 'Düğme bir yere götürür ve oyuncu vitrinimizde kalır.',
  provider: 'Sağlayıcının kendi lobisi çerçevede açılır. Oradan açılan oyun '
    + 'bizim launch kapımızdan geçmez: panelden kapatılmış bir oyun '
    + 'başlatılabilir, bahsi cüzdan tarafından reddedilir ve oyuncu reddi '
    + 'oyunun içinde görür.',
};

const GameLobbySettingsPanel = () => {
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  const canWrite = can('games.write');
  const [saved, setSaved] = useState(null);
  const [value, setValue] = useState(GAME_LOBBY_DEFAULT);
  const [modes, setModes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    client.get('/admin/game-lobby')
      .then(({ data }) => {
        if (!alive) return;
        setSaved(data.mode);
        setValue(data.mode);
        setModes(data.modes || []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (saved === null) return null;

  const dirty = value !== saved;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.patch('/admin/game-lobby', { mode: value });
      setSaved(data.mode);
      setValue(data.mode);
      toast({ title: 'Oyun içi lobi davranışı kaydedildi' });
    } catch (e) {
      toast({
        title: 'Oyun içi lobi davranışı kaydedilemedi',
        description: e.response?.data?.detail?.[0]?.msg
          || e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally {
      setSaving(false);
    }
  };

  const current = modes.find((mode) => mode.id === saved);

  return (
    <section className="gc-panel is-tone" style={{ '--gc-tone': '#38bdf8' }}>
      <div className="gc-panel-head">
        <span className="gc-card-icon"><DoorOpen size={19} /></span>
        <div className="gc-panel-title">
          <h2>Oyun İçi Lobi Düğmesi</h2>
          <p>
            Oyunun kendi arayüzündeki “lobi / ana sayfa” düğmesine basıldığında
            ne olacağını seçin.
          </p>
        </div>
      </div>

      <div className="gc-panel-body">
        <label className="gc-field">
          <span className="gc-label">Davranış</span>
          <select
            value={value}
            disabled={!canWrite}
            onChange={(event) => setValue(event.target.value)}
            className="gc-input"
            aria-label="Oyun içi lobi davranışı"
          >
            {modes.map((mode) => (
              <option key={mode.id} value={mode.id}>{mode.label}</option>
            ))}
          </select>
          <span className="gc-hint">{LOBBY_HINTS[value]}</span>
          <span className="gc-hint">
            Canlı casino masaları ve adı “Lobby/Lobi” olan oyunlar bu kuralın
            dışındadır: orada lobi oyunun kendisidir (masa seçimi) ve kapı
            uygulansaydı hiçbir masaya oturulamazdı. Demo turlarında da kapı
            çalışmaz — orada para yoktur.
          </span>
        </label>
      </div>

      {canWrite && (
        <div className="gc-panel-foot">
          <span className="gc-foot-note">
            Şu an kayıtlı: {current?.label || saved}
          </span>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="gc-btn is-primary"
          >
            {saving ? <Loader2 size={14} className="gc-spin" /> : <Save size={14} />}
            Davranışı Kaydet
          </button>
        </div>
      )}
    </section>
  );
};

export default GameLobbySettingsPanel;
